/**
 * Swati catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 3.
 *
 * `dir: 'ltr'`. siSwati at a mathematician's register; closely related to isiZulu. **Warrant →
 * «Sicinisekiso»** (justification/warrant — flagged). Scientific loanwords are used where
 * siSwati standardly borrows (logic → Lilojiki, modal → Imodali, heuristic, scalar, geometry).
 * W/M grades stay Latin (`::numbering-system/latn`). CLDR cardinal categories are one / other;
 * the noun uses the umu-/imi- class (umkhawulo → imikhawulo). Flagged "expert-review suggested"
 * in `Agent9_Batch3_REPORT.md`. Per INV-STABLE-ID a wrong translation is a presentation concern
 * only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const SS_CATALOG = {
    locale: 'ss',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Luphenyo',
        [P2.sectionClaims]: 'Timangalo',
        [P2.sectionIntegrity]: 'Bucotfo',
        [P2.axisWarrantLabel]: 'Sicinisekiso',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Kwekuhlola',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Inchazelo} interp {Kuhumusha} testable {Lokuhlolekako} tested {Lokuhloliwe} refuted {Lokuphikisiwe} program {Luhlelo lwelucwaningo} philos {Kwefilosofi} other {Akwatiwa}}',
        [P2.axisModalLabel]: 'Imodali',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Lizinga lemandla {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Lokuhloleka',
        [P2.stateRefuted]: 'Lokuphikisiwe',
        [P2.stateOutside]: 'Ngaphandle kwesilinganiso',
        [P2.verdictConflict]: 'Iyaphikisana ne-{constraints}',
        [P2.verdictOutside]: 'Idzinga i-{missing}',
        [P2.lossRegisterPlain]: 'Yentiwe lula elulwimini lolujwayelekile — leminye imininingwane yekunemba ishiyiwe.',
        [P2.lossRegisterKid]: 'Yentiwe lula kubafundzi labancane — imininingwane leminyenti ishiyiwe.',
        [P2.lossProfileDetail]: 'Iphrofayela lephelele ye-Sicinisekiso / Kwekuhlola / Imodali ifinyetiwe lapha.',
        [P2.lossConflictDetail]: 'Imininingwane yekungcubutana ifinyetiwe kuloku lokubukwako.',
        [P2.laneVerified]: 'Kucinisekisiwe',
        [P2.laneUnverified]: 'Akucinisekiswanga',
        [P2.laneConflict]: 'Kungcubutana kufakaziwe',
        [P2.constraintLogic]: 'Lilojiki',
        [P2.constraintLightBends]: 'Kukhanya kuyagobha enkhundleni yemandla ladonsako',
        [P2.constraintParallelPostulate]: 'I-postulate yemigca lefananako',
        [P2.claimScalarGravity]: 'Lithiyori ye-scalar yemandla ladonsako',
        [P2.claimNonEuclidean]: 'Ijiyomethri lengesiyo yeka-Euclid',
        [P2.claimSubstrateSr]: 'Kuhumusha kwe-substrate kwebudlelwane lobukhetsekile',
        [P2.edgeExtObservation]: 'Kubuka kwangaphandle',
        [P2.edgeResourceSample]: 'Insita yelikhompiyutha leyabiwe',
        [P2.edgeHeuristicSample]: 'Sicondziso se-heuristic',
        [P2.edgeSharedSample]: 'Sisekelo lesabiwe',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Imikhawulo',
        [P2.sectionEdges]: 'Imiphetfo',
        [P2.sectionFrame]: 'Luhlaka',
        [P2.sectionExposure]: 'Kuvetwa',
        [P2.statusAdoptedFormal]: 'Yemukelwe (ngalokusemtsetfweni)',
        [P2.statusAdoptedInterpretive]: 'Yemukelwe (ngekuhumusha)',
        [P2.statusSuspended]: 'Imisiwe',
        [P2.edgeEffectConstraint]: 'Yakha umkhawulo (ingaphikisa)',
        [P2.edgeEffectResource]: 'Insita — ayinamtselela etincumeni',
        [P2.edgeEffectHeuristic]: 'I-heuristic — ayinamtselela etincumeni',
        [P2.edgeEffectShared]: 'Sisekelo lesabiwe — ayinamtselela etincumeni',
        [P2.frameActive]: '{count, plural, =0 {Akukho imikhawulo lesebentako} one {# umkhawulo losebentako} other {# imikhawulo lesebentako}}',
        [P2.exposureNone]: 'Akukho kwentsembela ngekuhumusha',
        [P2.exposureDep]: 'Yentsembele ku-{id}',
        [P2.laneTrackMachine]: 'Umshini',
        [P2.laneTrackExpert]: 'Lochwepheshe',
        [P2.laneTrackCommunity]: 'Umphakatsi',
        [P2.expertAudited]: 'Kuhloliwe',
        [P2.expertInProgress]: 'Kuyachubeka',
        [P2.expertNotReviewed]: 'Akukabuyeketwa',
        [P2.communityFlags]: 'Kukhona timphawu',
        [P2.communityNone]: 'Atikho timphawu',
        [P2.controlChallenge]: 'Phikisa',
        [P2.controlActivate]: 'Yenta kusebente',
        [P2.controlMakeInterpretive]: 'Yenta kube ngekuhumusha',
        [P2.controlImportEdge]: 'Ngenisa umphetfo',
        [P2.controlPromote]: 'Khuphula',
        [P2.controlUndo]: 'Buyisela',
        [P2.controlRedo]: 'Yenta futsi',
        [P2.controlRegister]: 'Bhalisa',
        [P2.promoteRejected]: 'Kukhushulwa kwaliwe',
        [P2.promoteAccepted]: 'Kukhushulwa kwemukelwe',
        [P2.promoteReasonRefutationTerminal]: 'Bufakazi abukhoni kwelapha kuphikiswa.',
        [P2.promoteReasonKeystone]: 'Kukhushulwa lokubalulekile kudzinga lusito lwe-empirical.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Luhlobo lwe-empirical alulungisiwe.',
        [P2.promoteReasonModalNotFixed]: 'Lizinga le-modal alilungisiwe.',
        [P2.promoteReasonWarrantNotImproved]: 'Lizinga lesicinisekiso alitfutfukisiwe.',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=ss.js.map